import { useEffect, useState } from 'react'

import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import {
	createRootRouteWithContext,
	HeadContent,
	Link,
	Outlet,
	Scripts,
	useRouter,
} from '@tanstack/react-router'
import { TanStackRouterDevtools } from '@tanstack/react-router-devtools'

import PostHogPageView from '@/pageview'
import * as Sentry from '@sentry/react'
import { Check, Copy } from 'lucide-react'

import { isDEV } from '@/lib/constants'
import { clearQueryCache } from '@/lib/react-query'

import { NotFoundContent } from '@/features/app-shell/not-found-content'
import { SectionTitle } from '@/features/app-shell/section'
import { useNavigationTracking } from '@/hooks/use-action-tracking'

import {
	isApiError,
	isAuthError,
	isForbiddenError,
	isNetworkError,
	isNotFoundError,
	isServerError,
} from '@/shared/api/utils/error'

import { BackOrHomeButton } from '@/components/common/back-or-home-button'
import { Logo } from '@/components/icons/bitlyft-logo'
import { Button } from '@/components/ui/button'

import type {
	LoadedClerk,
	UseAuthReturn,
	UseUserReturn,
} from '@clerk/shared/types'
import type { QueryClient } from '@tanstack/react-query'
import type { ErrorComponentProps } from '@tanstack/react-router'

/**
 * Root layout that wraps the entire application.
 */
export const Route = createRootRouteWithContext<{
	queryClient: QueryClient
	clerk: {
		user: UseUserReturn | null
		auth: UseAuthReturn | null
		client: LoadedClerk | null
	}
}>()({
	head: () => ({
		meta: [
			{
				title: 'BitLyft AIR®',
			},
		],
	}),

	component: RootLayout,
	errorComponent: RootLayoutError,
	notFoundComponent: NotFoundContent,
})

function RootLayout() {
	useNavigationTracking()

	return (
		// 1Password addon creates an element with 1px height at the bottom of the screen, so we need to subtract it from the height to avoid creating a scrollbar
		<div className="relative flex h-[calc(100vh-1px)] w-full flex-row items-start justify-start overflow-hidden">
			<HeadContent />

			<Outlet />

			<PostHogPageView />

			{/* Only load dev tools on development mode */}
			{isDEV && (
				<>
					<ReactQueryDevtools initialIsOpen={false} />
					<TanStackRouterDevtools position="bottom-right" />
				</>
			)}

			<Scripts />
		</div>
	)
}

function getRootErrorDisplay(error: unknown) {
	if (isNetworkError(error))
		return {
			title: 'Connection issue',
			message: 'Check your internet connection and try again.',
			showRetry: true,
			showRef: false,
		}
	if (isAuthError(error))
		return {
			title: 'Session expired',
			message: 'Your session has expired. Please sign in again.',
			showRetry: false,
			showRef: false,
		}
	if (isForbiddenError(error))
		return {
			title: 'Access denied',
			message: 'You do not have permission to access this resource.',
			showRetry: false,
			showRef: false,
		}
	if (isNotFoundError(error))
		return {
			title: 'Not found',
			message: 'The requested resource could not be found.',
			showRetry: false,
			showRef: false,
		}
	if (isServerError(error))
		return {
			title: 'Something went wrong',
			message: 'We encountered a server error. Please try again later.',
			showRetry: true,
			showRef: true,
		}
	return {
		title: 'Unexpected Error',
		message: 'An unexpected error occurred. Please try again in a few minutes.',
		showRetry: true,
		showRef: true,
	}
}

function RootLayoutError({ error }: ErrorComponentProps) {
	if (isDEV) console.error(error)

	const router = useRouter()
	const [correlationId] = useState(() => crypto.randomUUID())
	const [copied, setCopied] = useState(false)

	useEffect(() => {
		Sentry.withScope((scope) => {
			scope.setTag('correlationId', correlationId)
			scope.setTag('errorSource', 'router')
			scope.setTag('feature', 'root')
			scope.setTag('caughtByBoundary', 'true')

			if (isApiError(error)) {
				scope.setFingerprint([
					'root-layout-error',
					String(error.status ?? 'unknown'),
				])
			}

			scope.setContext('rootLayoutError', {
				correlationId,
				path: window.location.pathname,
			})

			Sentry.captureException(error)
		})
	}, [error, correlationId])

	const display = getRootErrorDisplay(error)

	const handleReset = () => {
		router.invalidate()
		clearQueryCache()
	}

	const handleCopy = () => {
		void navigator.clipboard.writeText(correlationId)
		setCopied(true)
		setTimeout(() => setCopied(false), 2000)
	}

	return (
		<div className="flex h-screen w-full flex-col items-center justify-center gap-4 overflow-hidden">
			<Logo className="size-16" />
			<SectionTitle className="my-0">{display.title}</SectionTitle>

			<p className="text-muted-foreground text-center">{display.message}</p>

			{display.showRef && (
				<div className="flex items-center gap-1.5">
					<p className="text-muted-foreground text-sm">
						Reference: {correlationId.slice(0, 8).toUpperCase()}
					</p>
					<Button
						variant="ghost"
						size="icon"
						className="h-6 w-6"
						onClick={handleCopy}
					>
						{copied ? (
							<Check className="h-3 w-3" />
						) : (
							<Copy className="h-3 w-3" />
						)}
					</Button>
				</div>
			)}

			<div className="flex gap-2">
				{display.showRetry && (
					<Button variant="outline" onClick={handleReset}>
						Try Again
					</Button>
				)}
				{isAuthError(error) && (
					<Button nativeButton={false} render={<Link to="/login" />}>
						Go to Login
					</Button>
				)}
				{(isForbiddenError(error) || isNotFoundError(error)) && (
					<BackOrHomeButton />
				)}
			</div>
		</div>
	)
}
